import { useCallback } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { Tooltip } from '@/components/General/Tooltip';
import { showAlert } from '@/utils/showAlert';
import { parseTimeFrame } from '@/utils/dater';
import { Mutation } from '@/utils/react-query/Mutations';
import {
    getTouchTypeIcon,
    getTouchTypeLabel,
    getConnectionResultLabel,
    getDispositionLabel,
} from '@/utils/constants/followUp';

/**
 * TouchTimeline — person touch history with honest outcome semantics.
 *
 * Only connected results get "connected" styling; attempts (no answer,
 * voicemail, sent-without-reply) never look like connections. A negative
 * disposition is shown distinctly from a failed attempt. Retracted rows are
 * de-emphasized/struck through and never display as successful contact.
 *
 * Team notes are merged in: notes linked to a touch (followUpTouchID)
 * render inside that touch's card via `notesByTouchID`; standalone
 * FOLLOW_UP-visibility notes keep an honest "Other notes" subsection.
 */
const noteAttribution = (note) => {
    const first = note.Author?.firstName || 'Unknown';
    const lastInitial = (note.Author?.lastName || '').trim().charAt(0);
    return `— ${first} ${lastInitial}`.trimEnd();
};

const NoteBlock = ({ note }) => (
    <View testID={`touch-note-${note.id}`} style={styles.noteBlock}>
        <Text style={styles.noteContent} selectable>{note.content}</Text>
        <Text style={styles.noteMeta}>
            {`${noteAttribution(note)}, ${parseTimeFrame(note.createdAt, true)}`}
        </Text>
    </View>
);

const TouchTimeline = ({ touches = [], notesByTouchID = {}, standaloneNotes = [], loggedInUser, organizationID, personID, onCorrect }) => {
    const canManage = loggedInUser?.can?.('Manage Follow Up');

    const retractMutation = Mutation.followUp.retractTouch({
        onSuccess: () => Toast.show({ type: 'success', text2: 'Touch retracted' }),
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to retract the touch.' });
        },
    });

    const handleRetract = useCallback((touch) => {
        showAlert({
            title: 'Retract this touch?',
            subtitle: 'It will be marked invalid and no longer count toward summaries. The record is kept for audit — this is not a delete.',
            isDestructive: true,
            confirmText: 'Retract',
            onConfirm: () => retractMutation.mutate({ organizationID, personID, touchID: touch.id, reason: 'Retracted via app' }),
        });
    }, [organizationID, personID, retractMutation]);

    const renderTouch = (touch) => {
        const retracted = !!touch.retracted;
        // Correction/retraction: own touches, or any touch for a manager.
        const canEdit = !retracted && (touch.actorPersonID === loggedInUser?.id || canManage);
        const menuOptions = [];
        if (canEdit && onCorrect) {
            menuOptions.push({
                name: 'Correct Touch',
                icon: dropdownIcons.edit,
                onSelect: () => onCorrect(touch),
            });
        }
        if (canEdit) {
            menuOptions.push({
                name: 'Retract Touch',
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => handleRetract(touch),
            });
        }

        const connected = touch.isConnected && !retracted;

        return (
            <View key={touch.id} testID={`touch-row-${touch.id}`} style={[styles.row, retracted && styles.rowRetracted]}>
                <View style={[styles.iconWrap, connected && styles.iconWrapConnected]}>
                    <Unicon
                        name={getTouchTypeIcon(touch.type)}
                        size={16}
                        colorVariant={connected ? 'success' : 'textSecondary'}
                    />
                </View>
                <View style={styles.rowBody}>
                    <View style={styles.rowTitleLine}>
                        <Text style={[styles.rowTitle, retracted && styles.struck]}>
                            {getConnectionResultLabel(touch.type, touch.connectionResult)}
                        </Text>
                        <Text style={[styles.rowType, retracted && styles.struck]}>
                            {getTouchTypeLabel(touch.type)}
                        </Text>
                    </View>
                    <View style={styles.rowMeta}>
                        <Tooltip title={new Date(touch.occurredAt).toLocaleString()}>
                            <Text style={styles.rowTime}>{parseTimeFrame(touch.occurredAt, true)}</Text>
                        </Tooltip>
                        <Text style={styles.rowActor}>
                            {touch.actorName || 'Unknown'}{touch.actorPersonID === loggedInUser?.id ? ' (you)' : ''}
                        </Text>
                    </View>
                    <View style={styles.badgeRow}>
                        {retracted ? (
                            <Chip icon="close-circle-outline" colorVariant="error" testID={`touch-retracted-${touch.id}`}>
                                Retracted
                            </Chip>
                        ) : connected ? (
                            <Chip icon="checkmark-circle-outline" colorVariant="success">Connected</Chip>
                        ) : (
                            <Chip icon="arrow-forward-circle-outline" colorVariant="textSecondary">Attempt</Chip>
                        )}
                        {!!touch.responseDisposition && !retracted && (
                            <Chip
                                icon={touch.responseDisposition === 'REQUESTED_NO_CONTACT' ? 'hand-left-outline' : undefined}
                                colorVariant={
                                    touch.responseDisposition === 'REQUESTED_NO_CONTACT' ? 'error'
                                    : touch.responseDisposition === 'DECLINED' ? 'warning'
                                    : touch.responseDisposition === 'RECEPTIVE' ? 'success'
                                    : 'textSecondary'
                                }
                            >
                                {getDispositionLabel(touch.responseDisposition)}
                            </Chip>
                        )}
                        {(touch.noteCount || 0) > 0 && (
                            <Chip icon="document-text-outline" colorVariant="primary">
                                {touch.noteCount} note{touch.noteCount === 1 ? '' : 's'}
                            </Chip>
                        )}
                        {!!touch.correctedAt && !retracted && (
                            <Chip icon="pencil-outline" colorVariant="textSecondary">Corrected</Chip>
                        )}
                    </View>
                    {(notesByTouchID[touch.id] || []).map((note) => (
                        <NoteBlock key={note.id} note={note} />
                    ))}
                </View>
                {menuOptions.length > 0 && (
                    <DropdownMenu testID={`touch-menu-${touch.id}`} options={menuOptions} />
                )}
            </View>
        );
    };

    return (
        <View style={styles.list}>
            {touches.length === 0 ? (
                <View style={styles.emptyCard} testID="follow-up-touches-empty">
                    <EmptyListPlaceholder spacing={24} size={70} icon="user-round-arrow-left" message="No touches logged yet. Log a call, visit, or conversation to start the history." />
                </View>
            ) : (
                touches.map(renderTouch)
            )}
            {standaloneNotes.length > 0 && (
                <View style={styles.otherNotes} testID="follow-up-other-notes">
                    <Text style={styles.otherTitle}>Other notes</Text>
                    {standaloneNotes.map((note) => <NoteBlock key={note.id} note={note} />)}
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    emptyCard: { backgroundColor: colors.surface, borderRadius: 12, padding: 12, boxShadow: shadows.card },
    list: { gap: 8 },
    row: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
    },
    rowRetracted: { opacity: 0.55 },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.surfaceVariant,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
    },
    iconWrapConnected: { backgroundColor: colors.successContainer },
    rowBody: { flex: 1, minWidth: 0 },
    rowTitleLine: { flexDirection: 'row', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
    rowTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    rowType: { fontSize: 12, color: colors.textSecondary },
    struck: { textDecorationLine: 'line-through' },
    rowMeta: { flexDirection: 'row', gap: 8, marginTop: 2, flexWrap: 'wrap' },
    rowTime: { fontSize: 12, color: colors.textSecondary },
    rowActor: { fontSize: 12, color: colors.textSecondary },
    badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
    noteBlock: {
        marginTop: 8,
        marginLeft: 6,
        paddingLeft: 10,
        borderLeftWidth: 2,
        borderLeftColor: colors.pathsContainer,
        gap: 2,
    },
    noteContent: { fontSize: 13, color: colors.text, lineHeight: 19 },
    noteMeta: { fontSize: 11, color: colors.textSecondary },
    otherNotes: { gap: 6, marginTop: 4 },
    otherTitle: { fontSize: 12, fontFamily: fonts.heading, color: colors.textSecondary },
}));

export default TouchTimeline;
